import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2 as GameIcon } from 'lucide-react';
import { GameInvitationPayload } from '@fake-stack-overflow/shared/types/socket';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '../../ui/toast';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import useUserContext from '../../../hooks/useUserContext';

/**
 * Component that listens for game invitations via socket and displays notification toasts
 */
const GameInvitationNotification: React.FC = () => {
  const { socket } = useUserContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<GameInvitationPayload[]>([]);

  const handleAcceptInvitation = useCallback(
    (gameID: string) => {
      setInvitations(prev => prev.filter(inv => inv.gameID !== gameID));

      navigate(`/games/${gameID}`);
    },
    [navigate],
  );

  const handleDismissInvitation = (gameID: string) => {
    setInvitations(prev => prev.filter(inv => inv.gameID !== gameID));
  };

  useEffect(() => {
    if (!socket) return undefined;

    const handleGameInvitation = (invitation: GameInvitationPayload) => {
      setInvitations(prev => [...prev, invitation]);

      toast({
        title: 'Game Invitation',
        description: `${invitation.fromUsername} invited you to a ${invitation.gameType} game!`,
        action: (
          <Button
            variant='default'
            onClick={() => handleAcceptInvitation(invitation.gameID)}
            className='bg-blue-600 hover:bg-blue-700'>
            Join Game
          </Button>
        ),
      });
    };

    socket.on('gameInvitation', handleGameInvitation);

    return () => {
      socket.off('gameInvitation', handleGameInvitation);
    };
  }, [socket, toast, navigate, handleAcceptInvitation]);

  if (invitations.length === 0) return null;

  return (
    <ToastProvider>
      {invitations.map(invitation => (
        <Toast
          key={`${invitation.fromUsername}-${invitation.gameID}`}
          className='fixed bottom-4 right-4'>
          <div className='flex items-center gap-2'>
            <GameIcon className='h-5 w-5 text-blue-600' />
            <div>
              <ToastTitle>Game Invitation</ToastTitle>
              <ToastDescription>
                {invitation.fromUsername} invited you to a {invitation.gameType} game!
              </ToastDescription>
            </div>
          </div>
          <div className='mt-4 flex gap-2'>
            <Button
              size='sm'
              onClick={() => handleAcceptInvitation(invitation.gameID)}
              className='bg-blue-600 hover:bg-blue-700'>
              Join Game
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={() => handleDismissInvitation(invitation.gameID)}>
              Dismiss
            </Button>
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
};

export default GameInvitationNotification;
